import { SimEventKind } from "@highmarch/domain";
import type { SimEvent } from "@highmarch/domain";

/**
 * Which of one tick's `EnemyDamaged` events are direct hits (index-aligned flags). The event carries no damage type,
 * so a burn's damage-over-time tick is told apart by where it sits in the fixed system order: troops strike, then
 * burns tick, then towers fire (LevelSim.step). Everything after the tick's first `TowerFired` is a tower's round,
 * chain or splash; before it, only a troop's blow (the event right after its `TroopAttacked`) is a hit. A burning
 * enemy otherwise played `hit` every tick, eighteen times a second.
 */
export function directHits(events: readonly SimEvent[]): boolean[] {
  let towersFiring = false;
  return events.map((ev, i) => {
    if (ev.kind === SimEventKind.TowerFired) towersFiring = true;
    if (ev.kind !== SimEventKind.EnemyDamaged) return false;
    if (towersFiring) return true;
    const prev = i > 0 ? events[i - 1] : null;
    return prev !== null && prev.kind === SimEventKind.TroopAttacked && prev.b === ev.a;
  });
}

/** Enemies whose death gets the heavy cue besides bosses: the big armoured bodies. */
const HEAVY_ENEMY = /brute|shieldbearer|boss_/;

/** `deathBig` for a boss or a heavy archetype, `death` otherwise (and when the body is already gone). */
export function deathCue(enemy: { isBoss: boolean; archetype: number } | null, archetypeId: (archetype: number) => string): "death" | "deathBig" {
  if (!enemy) return "death";
  return enemy.isBoss || HEAVY_ENEMY.test(archetypeId(enemy.archetype)) ? "deathBig" : "death";
}

/**
 * A placed card's sound: a tower is built (a wood-and-stone thump with a pizzicato confirm), a road segment is laid
 * (lighter, untuned cobble clacks). Both used to play the same `place`, so the road read as a tower going down.
 */
export function placeCue(kind: SimEventKind.TowerPlaced | SimEventKind.SegmentPlaced): "towerPlace" | "roadPlace" {
  return kind === SimEventKind.TowerPlaced ? "towerPlace" : "roadPlace";
}

/** The tier-up for a tower that has just reached `tier` of its `tiers`: the top tier has its own, higher, longer cue. */
export function upgradeCue(tier: number, tiers: number): "towerUpgrade" | "towerUpgradeMax" {
  return tier >= tiers ? "towerUpgradeMax" : "towerUpgrade";
}

/** What `beaconBuffs` reads of a tower slot. */
interface AuraSlot { readonly alive: boolean; readonly cell: { readonly x: number; readonly y: number }; readonly auraDamageMult: { toNumber(): number } }

/**
 * Whether a Beacon's pulse has anyone to lift (A2): another living tower inside its `radius` (cells) that the sim has
 * buffed (auraDamageMult above 1). Only then does the pulse ring its bowl; a lone beacon's pulse is seen, not heard, so
 * the cue means "the beacon is working".
 */
export function beaconBuffs(towers: readonly AuraSlot[], beacon: AuraSlot, radius: number): boolean {
  return towers.some((o) => o !== beacon && o.alive && o.auraDamageMult.toNumber() > 1
    && Math.hypot(o.cell.x - beacon.cell.x, o.cell.y - beacon.cell.y) <= radius);
}

/** What a wave's end needs to know about the run (M38 `waveCue`). */
export interface WaveRun {
  /** The level's stage count. */
  readonly stages: number;
  /** balance.economy.checkpointEveryStages (0 = no checkpoints). */
  readonly checkpointEvery: number;
  /** A campaign level: it ends on a boss stage and has checkpoints (not Endless, not Iron). */
  readonly campaign: boolean;
}

/** Every this-many waves is a milestone. */
export const WAVE_MILESTONE_EVERY = 5;
/** A milestone call needs this many waves since the last one; nearer, it is a tick (wave 5 and the checkpoint at 6). */
export const WAVE_MILESTONE_GAP = 2;

/**
 * The cue for wave `cleared` ending (M38, the owner: "the sound effect for the end of a wave ... as you hear it that
 * often it's becoming annoying"): a short brass `waveMilestone` on every fifth wave, the wave before the boss and a
 * checkpoint mark, unless one played within the last WAVE_MILESTONE_GAP waves (`lastMilestone`, 0 for none); a soft
 * `waveTick` on every other wave; nothing on the level's last stage (the win fanfare has it).
 */
export function waveCue(cleared: number, run: WaveRun, lastMilestone: number): "waveTick" | "waveMilestone" | null {
  if (cleared <= 0 || (run.campaign && cleared >= run.stages)) return null;
  const milestone = cleared % WAVE_MILESTONE_EVERY === 0
    || (run.campaign && cleared === run.stages - 1)
    || (run.campaign && run.checkpointEvery > 0 && cleared % run.checkpointEvery === 0);
  return milestone && cleared - lastMilestone >= WAVE_MILESTONE_GAP ? "waveMilestone" : "waveTick";
}
